import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { logout } from "./authSlice";
import {
  deleteNotification as deleteNotificationApi,
  getNotifications,
  getUnreadCount,
  markRead,
} from "../../Services/notificationsService";

const initialState = {
  items: [],
  unreadCount: 0,
  loading: false,
  hasMore: true,
  page: 1,
  error: null,
};

export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async ({ page = 1, limit = 20, unreadOnly = false } = {}, { rejectWithValue }) => {
    try {
      const response = await getNotifications({ page, limit, unreadOnly });
      return response.data?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  "notifications/fetchUnreadCount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUnreadCount();
      return response.data?.data?.unreadCount || 0;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

export const markNotificationsRead = createAsyncThunk(
  "notifications/markNotificationsRead",
  async ({ ids = [] } = {}, { rejectWithValue }) => {
    try {
      const response = await markRead(ids);
      return {
        updatedIds: response.data?.data?.updatedIds || [],
        unreadCount: response.data?.data?.unreadCount || 0,
      };
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

export const removeNotification = createAsyncThunk(
  "notifications/removeNotification",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteNotificationApi(id);
      return {
        id,
        unreadCount: response.data?.data?.unreadCount,
      };
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

export const clearAllNotifications = createAsyncThunk(
  "notifications/clearAllNotifications",
  async (ids, { rejectWithValue }) => {
    try {
      await Promise.all(ids.map((id) => deleteNotificationApi(id)));
      return { unreadCount: 0 };
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    notificationReceived: (state, action) => {
      const incoming = action.payload;
      if (!incoming?.notification_id) {
        return;
      }

      const exists = state.items.some(
        (item) => item.notification_id === incoming.notification_id
      );
      if (exists) {
        return;
      }

      state.items.unshift(incoming);
      if (!incoming.is_read) {
        state.unreadCount += 1;
      }
    },
    updateUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
    },
    resetNotifications: (state) => {
      state.items = [];
      state.unreadCount = 0;
      state.loading = false;
      state.hasMore = true;
      state.page = 1;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;

        const notifications = action.payload?.notifications || [];
        const pagination = action.payload?.pagination || {};

        if (pagination.page > 1) {
          state.items = [...state.items, ...notifications];
        } else {
          state.items = notifications;
        }

        state.page = pagination.page || 1;
        state.hasMore = Boolean(pagination.hasMore);
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch notifications";
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })
      .addCase(markNotificationsRead.fulfilled, (state, action) => {
        const { updatedIds, unreadCount } = action.payload;

        if (updatedIds.length === 0) {
          state.items = state.items.map((item) => ({
            ...item,
            is_read: true,
          }));
        } else {
          const updated = new Set(updatedIds);
          state.items = state.items.map((item) => {
            if (!updated.has(item.notification_id)) {
              return item;
            }
            return {
              ...item,
              is_read: true,
            };
          });
        }

        state.unreadCount = unreadCount;
      })
      .addCase(removeNotification.fulfilled, (state, action) => {
        state.items = state.items.filter((item) => item.notification_id !== action.payload.id);
        if (typeof action.payload.unreadCount === "number") {
          state.unreadCount = action.payload.unreadCount;
        }
      })
      .addCase(clearAllNotifications.fulfilled, (state, action) => {
        state.items = [];
        state.unreadCount = action.payload.unreadCount;
      })
      .addCase(logout, (state) => {
        state.items = [];
        state.unreadCount = 0;
        state.loading = false;
        state.hasMore = true;
        state.page = 1;
        state.error = null;
      });
  },
});

export const { notificationReceived, resetNotifications, updateUnreadCount } =
  notificationSlice.actions;

export default notificationSlice.reducer;
